import { assert, assertNonNullable } from '@disy/lib/custom-error';
import type { DelegateEvent } from '@disy/lib/event-util';
import { on } from '@disy/lib/event-util';

import { getStore } from 'cadenza/store/cadenza-store';
import { navigateWithinWorkbook } from 'cadenza/utils/confirm-navigation-util';
import type { DataViewDto } from 'cadenza/workbook/api/data-view-dto';
import type { ViewDto } from 'cadenza/workbook/api/view-dto';
import type { WorkbookLayerTreeNode } from 'cadenza/workbook/api/workbook-layer-tree-node';
import type { DataViewId } from 'cadenza/workbook/model/data-view-id.gen';
import type { View } from 'cadenza/workbook/view/view';
import type { EmptyView } from 'cadenza/workbook/view/view-empty/empty-view';
import type { Workbook } from 'cadenza/workbook/workbook';
import { getViewByDataViewId } from 'cadenza/workbook/workbook-core-selectors.state';

let workbook: Workbook | null; // Cached workbook

/** @deprecated Use actions and selectors from `workbook.state.js` instead */
export function getWorkbook(): Workbook {
  if (!workbook) {
    workbook = window.document.querySelector<Workbook>('d-workbook');
  }
  assertNonNullable(workbook);
  return workbook;
}

export function isDataViewValidForMapAnimation(dataView: DataViewDto) {
  if (dataView.attributes.GEOMETRY === undefined) {
    return false;
  }
  const noOfAttributes = dataView.attributes.GEOMETRY.length;
  const minAttributes = dataView.attributeBundleDefinitions.GEOMETRY!.minNumberOfAttributes;
  return !minAttributes || noOfAttributes >= minAttributes;
}

/**
 * Global listener for workbookNameAndHash links that includes 'embeddingTargetId'
 * query parameter in the navigated URL, if it is present in the current URL.
 */
export function keepEmbeddingTargetWhenFollowingLinks() {
  return on(
    document,
    'click',
    (event: DelegateEvent) => {
      event.preventDefault();
      const linkUrl = (event.delegateTarget as HTMLAnchorElement).href;
      navigateWithinWorkbook(linkUrl);
    },
    { delegate: 'a[href*="/workbooks"][href*="workbookHash="]' },
  );
}

/** @see net/disy/cadenza/workbook/webreport/web/rest/WorkbookReportEndpointController#SORT_BY_ROW_AND_COLUMN */
export function compareViewByRowAndColumn(viewA: View | EmptyView | ViewDto, viewB: View | EmptyView | ViewDto) {
  if (!viewA.layout || !viewB.layout) {
    return 0;
  }
  const [rowStartA, columnStartA] = viewA.layout;
  const [rowStartB, columnStartB] = viewB.layout;
  const comparedByRowStart = rowStartA - rowStartB;
  if (comparedByRowStart !== 0) {
    return comparedByRowStart;
  }
  return columnStartA - columnStartB;
}

export function compareDataViewsByAppearance(dataViewA: DataViewId, dataViewB: DataViewId) {
  // Don't break when having the same data view multiple times
  if (dataViewA === dataViewB) {
    return 0;
  }

  const viewA = getViewByDataViewId(getStore().getState(), dataViewA);
  const viewB = getViewByDataViewId(getStore().getState(), dataViewB);

  if (viewA.id === viewB.id) {
    return compareDataViewsOfSameViewByAppearance(dataViewA, dataViewB, viewA);
  }
  return compareViewByRowAndColumn(viewA, viewB);
}

export function compareDataViewsOfSameViewByAppearance(dataView1: DataViewId, dataViewB: DataViewId, view: ViewDto) {
  assert(
    view.dataViews.includes(dataView1) &&
      view.dataViews.includes(dataViewB) &&
      (view.type === 'MAP' || view.type === 'CHART'),
    'Only maps and charts as views with multiple data views are currently supported to get sorted',
  );
  // Handle equal case for general compatibility with Array.sort()
  if (dataView1 === dataViewB) {
    return 0;
  }
  if (view.type === 'MAP') {
    return compareLayerIdsByVerticalPresenceInLayerTree(dataView1, dataViewB, view.layerTree);
  } else {
    return compareDataViewIdsByOrderInList(dataView1, dataViewB, view.dataViews);
  }
}

function compareDataViewIdsByOrderInList(a: DataViewId, b: DataViewId, dataViews: DataViewId[]) {
  // reverse the order without creating new array because this is used as comparator, and called multiple times during sorting process.
  for (let i = dataViews.length - 1; i >= 0; i--) {
    const dataViewId = dataViews[i];
    if (dataViewId === a) {
      return -1;
    }
    if (dataViewId === b) {
      return 1;
    }
  }
  throw new Error('Neither dataView a or b found in the list');
}

function compareLayerIdsByVerticalPresenceInLayerTree(
  a: DataViewId,
  b: DataViewId,
  layerTree: WorkbookLayerTreeNode[],
) {
  // It could be faster to inline this and return early, but separate steps are easier to debug and
  // performance impact is negligible.
  const flattenedTree = flattenTree(layerTree);

  for (const layer of flattenedTree) {
    if (layer.id === a) {
      return -1;
    }
    if (layer.id === b) {
      return 1;
    }
  }

  throw new Error('Neither dataView layer a or b found in tree');
}

function flattenTree(layerTree: WorkbookLayerTreeNode[]): WorkbookLayerTreeNode[] {
  return layerTree.flatMap((layer) => {
    return [layer, ...flattenTree(layer.children)];
  });
}
